import { motion } from 'framer-motion';
import { CameraFill } from '@peacecover/icon';
import type { ReactNode } from 'react';
import type { components } from '../../../types/schema';

type CastRelationship = components['schemas']['ApiV1CastRelationship'];

interface CurrentCastRelationshipSectionProps {
  currentCastRelationship: CastRelationship | undefined;
  children: ReactNode;
  gap?: number;
  onPersonAClick?: () => void;
  onPersonBClick?: () => void;
  showCameraIcon?: boolean;
}

const SEQUENCE_DELAY = 0.6;
const CYCLE_DURATION = 3.6;
const ANIMATION_DURATION = 0.5;
const REPEAT_DELAY = CYCLE_DURATION - ANIMATION_DURATION;

const starAnimationVariants = {
  animate: (customDelay: number) => ({
    opacity: [0.4, 0.9, 0.4],
    scale: [1.0, 1.08, 1.0],
    filter: ['blur(0px)', 'blur(2px)', 'blur(0px)'],
    transition: {
      duration: ANIMATION_DURATION,
      ease: 'easeInOut',
      delay: customDelay,
      repeat: Number.POSITIVE_INFINITY,
      repeatDelay: REPEAT_DELAY
    }
  })
};

const CurrentCastRelationshipSection = ({
  currentCastRelationship,
  children,
  gap = 4,
  onPersonAClick,
  onPersonBClick,
  showCameraIcon = false
}: CurrentCastRelationshipSectionProps) => {
  return (
    <div className="flex items-center justify-center" style={{ gap: `${gap}px` }}>
      {/* Left Person */}
      <button
        type="button"
        className={`flex flex-col items-center gap-[1px] ${onPersonAClick ? 'cursor-pointer' : ''}`}
        onClick={onPersonAClick}
      >
        <div className="relative w-[120px] h-[120px] flex items-center justify-center">
          {currentCastRelationship?.personAThumbnailUrl && (
            <img
              src={currentCastRelationship?.personAThumbnailUrl}
              alt="personAThumbnailUrl"
              className="rounded-full object-cover w-[88px] h-[88px]"
            />
          )}
          {showCameraIcon && (
            <div className="absolute bottom-[12px] right-[18px] p-[2px] bg-textPrimary-dark rounded-full border-[2px] border-[#161616] z-10">
              <CameraFill size={14} className="text-background-dark" />
            </div>
          )}
          <BubbleGlowLeft />

          {/* left 1: 0.0s */}
          <motion.div
            className="absolute top-[17px] left-[11px]"
            custom={0 * SEQUENCE_DELAY}
            variants={starAnimationVariants}
            animate="animate"
            initial={{ opacity: 0.4, scale: 1.0 }}
          >
            <BigStar />
          </motion.div>

          {/* left 2: 1.2s (Index 2 in global timeline) -> Step 2 */}
          {/* 0.0s (L1) -> 0.6s (R3) -> 1.2s (L2) */}
          <motion.div
            className="absolute bottom-[31px] left-[33px]"
            custom={2 * SEQUENCE_DELAY}
            variants={starAnimationVariants}
            animate="animate"
            initial={{ opacity: 0.4, scale: 1.0 }}
          >
            <SmallStar />
          </motion.div>

          {/* left 3: 2.4s (Index 4 in global timeline) */}
          <motion.div
            className="absolute bottom-[17px] right-[10px]"
            custom={4 * SEQUENCE_DELAY}
            variants={starAnimationVariants}
            animate="animate"
            initial={{ opacity: 0.4, scale: 1.0 }}
          >
            <SmallStar />
          </motion.div>
        </div>
        <span className="text-[12px] font-medium leading-[14px] text-main-dark-1">
          {currentCastRelationship?.personAName}
        </span>
      </button>

      {children}

      {/* Right Person */}
      <button
        type="button"
        className={`flex flex-col items-center gap-[1px] ${onPersonBClick ? 'cursor-pointer' : ''}`}
        onClick={onPersonBClick}
      >
        <div className="relative w-[120px] h-[120px] flex items-center justify-center">
          {currentCastRelationship?.personBThumbnailUrl && (
            <img
              src={currentCastRelationship?.personBThumbnailUrl}
              alt="personBThumbnailUrl"
              className="rounded-full object-cover w-[88px] h-[88px]"
            />
          )}
          {showCameraIcon && (
            <div className="absolute bottom-[12px] right-[18px] p-[2px] bg-textPrimary-dark rounded-full border-[2px] border-[#161616] z-10">
              <CameraFill size={14} className="text-background-dark" />
            </div>
          )}
          <img
            src="/assets/scene/bubble_glow_right.png"
            alt="profile_bubble"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />

          {/* right 3: 0.6s (Index 1) */}
          <motion.div
            className="absolute top-[11px] right-[21px]"
            custom={1 * SEQUENCE_DELAY}
            variants={starAnimationVariants}
            animate="animate"
            initial={{ opacity: 0.4, scale: 1.0 }}
          >
            <BigStar />
          </motion.div>

          {/* right 2: 1.8s (Index 3) */}
          <motion.div
            className="absolute bottom-[44px] right-[45px]"
            custom={3 * SEQUENCE_DELAY}
            variants={starAnimationVariants}
            animate="animate"
            initial={{ opacity: 0.4, scale: 1.0 }}
          >
            <SmallStar />
          </motion.div>

          {/* right 1: 3.0s (Index 5) */}
          <motion.div
            className="absolute bottom-[15px] left-[11px]"
            custom={5 * SEQUENCE_DELAY}
            variants={starAnimationVariants}
            animate="animate"
            initial={{ opacity: 0.4, scale: 1.0 }}
          >
            <SmallStar />
          </motion.div>
        </div>
        <span className="text-[12px] font-medium leading-[14px] text-main-dark-1">
          {currentCastRelationship?.personBName}
        </span>
      </button>
    </div>
  );
};

export default CurrentCastRelationshipSection;

export const DoubleArrow = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="73"
      height="8"
      viewBox="0 0 73 8"
      fill="none"
      aria-label="double-arrow"
      role="img"
      className="mt-[20px]"
    >
      <path
        d="M0.146447 3.32809C-0.0488155 3.52335 -0.0488155 3.83993 0.146447 4.03519L3.32843 7.21717C3.52369 7.41244 3.84027 7.41244 4.03553 7.21717C4.2308 7.02191 4.2308 6.70533 4.03553 6.51007L1.20711 3.68164L4.03553 0.853214C4.2308 0.657951 4.2308 0.341369 4.03553 0.146107C3.84027 -0.0491555 3.52369 -0.0491555 3.32843 0.146107L0.146447 3.32809ZM72.8536 4.03519C73.0488 3.83993 73.0488 3.52335 72.8536 3.32809L69.6716 0.146107C69.4763 -0.0491555 69.1597 -0.0491555 68.9645 0.146107C68.7692 0.341369 68.7692 0.657951 68.9645 0.853214L71.7929 3.68164L68.9645 6.51007C68.7692 6.70533 68.7692 7.02191 68.9645 7.21717C69.1597 7.41244 69.4763 7.41244 69.6716 7.21717L72.8536 4.03519ZM0.5 3.68164V4.18164H72.5V3.68164V3.18164H0.5V3.68164Z"
        fill="#D6D6D6"
      />
    </svg>
  );
};

const BubbleGlowLeft = () => {
  return (
    <img
      src="/assets/scene/bubble_glow_left.png"
      alt="profile_bubble"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    />
  );
};

const BigStar = () => {
  return (
    <svg
      width="27"
      height="34"
      viewBox="0 0 27 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="big-star"
      role="img"
    >
      <g filter="url(#filter0_df_1256_9993)">
        <path
          d="M13.0283 5.09998L14.5562 12.1267L19.9565 10.35L16.084 15.6L19.9565 20.85L14.5562 19.0733L13.0283 26.1L11.5005 19.0733L6.10012 20.85L9.97259 15.6L6.10012 10.35L11.5005 12.1267L13.0283 5.09998Z"
          fill="url(#paint0_radial_1256_9993)"
          shapeRendering="crispEdges"
        />
      </g>
      <g filter="url(#filter1_f_1256_9993)">
        <path
          d="M13.1045 10.1L13.7302 13.8054L15.9419 12.8685L14.3559 15.637L15.9419 18.4055L13.7302 17.4686L13.1045 21.174L12.4788 17.4686L10.2671 18.4055L11.853 15.637L10.2671 12.8685L12.4788 13.8054L13.1045 10.1Z"
          fill="url(#paint1_radial_1256_9993)"
        />
      </g>
      <defs>
        <filter
          id="filter0_df_1256_9993"
          x="9.77516e-05"
          y="-2.43187e-05"
          width="26.0564"
          height="33.2"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="3.05" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.81 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1256_9993" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_1256_9993"
            result="shape"
          />
          <feGaussianBlur stdDeviation="2" result="effect2_foregroundBlur_1256_9993" />
        </filter>
        <filter
          id="filter1_f_1256_9993"
          x="9.26709"
          y="9.09998"
          width="7.6748"
          height="13.0741"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="0.5" result="effect1_foregroundBlur_1256_9993" />
        </filter>
        <radialGradient
          id="paint0_radial_1256_9993"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(13.0283 15.6) rotate(90) scale(10.5 8)"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="paint1_radial_1256_9993"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(13.1045 15.637) rotate(90) scale(5.53704 3.27635)"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
};

const SmallStar = () => {
  return (
    <svg
      width="21"
      height="27"
      viewBox="0 0 21 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="small-star"
      role="img"
    >
      <g filter="url(#filter0_df_1256_9996)">
        <path
          d="M10.4302 5.09998L11.3851 9.78443L14.7603 8.59998L12.34 12.1L14.7603 15.6L11.3851 14.4155L10.4302 19.1L9.47526 14.4155L6.10005 15.6L8.52035 12.1L6.10005 8.59998L9.47526 9.78443L10.4302 5.09998Z"
          fill="url(#paint0_radial_1256_9996)"
          fillOpacity="0.64"
          shapeRendering="crispEdges"
        />
      </g>
      <g opacity="0.51" filter="url(#filter1_f_1256_9996)">
        <path
          d="M10.6028 7.92163L11.081 10.7537L12.7715 10.0376L11.5593 12.1537L12.7715 14.2697L11.081 13.5536L10.6028 16.3857L10.1245 13.5536L8.43413 14.2697L9.64629 12.1537L8.43413 10.0376L10.1245 10.7537L10.6028 7.92163Z"
          fill="url(#paint1_radial_1256_9996)"
        />
      </g>
      <defs>
        <filter
          id="filter0_df_1256_9996"
          x="9.77516e-05"
          y="-2.43187e-05"
          width="20.8602"
          height="26.2"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="3.05" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.81 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1256_9996" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_1256_9996"
            result="shape"
          />
          <feGaussianBlur stdDeviation="1" result="effect2_foregroundBlur_1256_9996" />
        </filter>
        <filter
          id="filter1_f_1256_9996"
          x="7.93408"
          y="7.42163"
          width="5.3374"
          height="9.46411"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="0.25" result="effect1_foregroundBlur_1256_9996" />
        </filter>
        <radialGradient
          id="paint0_radial_1256_9996"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(10.4302 12.1) rotate(90) scale(7 5)"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="paint1_radial_1256_9996"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(10.6028 12.1537) rotate(90) scale(4.23203 2.50416)"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
};
